'use client';

import {
  useSignInWithGoogle,
  useSignInWithLinkedIn,
  useSignInWithMicrosoft,
  useUser,
} from '@/store/auth-store';
import { useEffect, useRef, useState } from 'react';

export default function VideoUpload() {
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const messageRef = useRef<HTMLDivElement>(null);

  const signInWithGoogle = useSignInWithGoogle();
  const signInWithLinkedIn = useSignInWithLinkedIn();
  const signInWithMicrosoft = useSignInWithMicrosoft();
  const user = useUser();

  useEffect(() => {
    if (submitMessage && messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitMessage]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowHelpModal(false);
    };
    if (showHelpModal) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [showHelpModal]);

  const toggleEmailForm = () => {
    setShowEmailForm(!showEmailForm);
  };

  const submitVideoData = async (
    videoUrl: string,
    name: string,
    email: string,
    company: string,
    platform: string
  ) => {
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/video', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
          company,
          videoUrl,
          platform,
          browserData: {
            userAgent: navigator.userAgent,
            referrer: document.referrer || 'Direct',
          },
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage(
          'Thank you! Your video has been submitted. We will review it and get back to you within 24 hours.'
        );
        // Reset form
        const videoInput = document.getElementById('video-url') as HTMLInputElement;
        if (videoInput) videoInput.value = '';
        setShowEmailForm(false);
      } else {
        setSubmitMessage(`Error: ${result.error || 'Failed to submit video.'}`);
      }
    } catch (error) {
      console.error('Video submission error:', error);
      setSubmitMessage('Error: An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = async (e: React.MouseEvent) => {
    e.preventDefault();
    const videoInput = document.getElementById('video-url') as HTMLInputElement;
    if (!videoInput?.checkValidity()) {
      videoInput?.reportValidity();
      return;
    }

    const videoUrl = videoInput.value;

    // Store video URL and provider in sessionStorage to retrieve after OAuth
    sessionStorage.setItem('pendingVideoUrl', videoUrl);
    sessionStorage.setItem('pendingAuthProvider', 'google');

    // Initiate Google OAuth
    await signInWithGoogle(window.location.href);
  };

  const handleLinkedInAuth = async (e: React.MouseEvent) => {
    e.preventDefault();
    const videoInput = document.getElementById('video-url') as HTMLInputElement;
    if (!videoInput?.checkValidity()) {
      videoInput?.reportValidity();
      return;
    }

    const videoUrl = videoInput.value;

    // Store video URL and provider in sessionStorage to retrieve after OAuth
    sessionStorage.setItem('pendingVideoUrl', videoUrl);
    sessionStorage.setItem('pendingAuthProvider', 'linkedin');

    // Initiate LinkedIn OAuth
    await signInWithLinkedIn(window.location.href);
  };

  const handleMicrosoftAuth = async (e: React.MouseEvent) => {
    e.preventDefault();
    const videoInput = document.getElementById('video-url') as HTMLInputElement;
    if (!videoInput?.checkValidity()) {
      videoInput?.reportValidity();
      return;
    }

    const videoUrl = videoInput.value;

    // Store video URL and provider in sessionStorage to retrieve after OAuth
    sessionStorage.setItem('pendingVideoUrl', videoUrl);
    sessionStorage.setItem('pendingAuthProvider', 'microsoft');

    // Initiate Microsoft OAuth
    await signInWithMicrosoft(window.location.href);
  };

  const handleQuickContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const videoInput = document.getElementById('video-url') as HTMLInputElement;
    const nameInput = form.querySelector('#name') as HTMLInputElement;
    const emailInput = form.querySelector('#email') as HTMLInputElement;
    const companyInput = form.querySelector('#company') as HTMLInputElement;

    if (!videoInput?.value) {
      alert('Please enter a video URL first.');
      return;
    }

    await submitVideoData(
      videoInput.value,
      nameInput.value,
      emailInput.value,
      companyInput.value,
      'email'
    );

    form.reset();
  };

  // Check if user just authenticated and has pending video
  if (user && typeof window !== 'undefined') {
    const pendingVideoUrl = sessionStorage.getItem('pendingVideoUrl');
    if (pendingVideoUrl) {
      const provider = sessionStorage.getItem('pendingAuthProvider') || 'google';
      sessionStorage.removeItem('pendingVideoUrl');
      sessionStorage.removeItem('pendingAuthProvider');

      // Determine platform from last action
      // @ts-ignore - custom fields
      const userName =
        user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'User';
      submitVideoData(pendingVideoUrl, userName, user.email, '', provider);
    }
  }

  return (
    <div className="step-upload" style={{ display: 'block', opacity: 1, visibility: 'visible' }}>
      <h3>Step 1 — Show us your pain</h3>
      <p>
        Create a short video screen recording (2–5 minutes). <br />
        Walk us through what you do manually — we&apos;ll handle the rest.
      </p>

      <div className="upload-options">
        <div className="upload-area">
          <div className="upload-input-group">
            <label htmlFor="video-url" className="sr-only">
              Paste video URL
            </label>
            <input
              type="url"
              id="video-url"
              placeholder="Paste video URL (Loom, OneDrive, Google Drive, etc)"
              required
              aria-label="Paste video URL"
            />
            <button
              className="help-icon"
              onClick={() => setShowHelpModal(true)}
              title="How to record & upload a Loom video"
              aria-label="Help: How to record and upload a video"
            >
              <i className="fas fa-question-circle" />
            </button>
          </div>
        </div>
      </div>

      <div
        className="continue-options-wrapper"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'center',
          marginTop: '1.5rem',
        }}
      >
        {/* Row 1: Google */}
        <div>
          <button
            className="continue-option google-button"
            onClick={handleGoogleAuth}
            aria-label="Continue with Google"
          >
            <i className="fab fa-google" />
            <span>Continue with Google</span>
          </button>
        </div>

        {/* Row 2: Others */}
        <div className="continue-options-row" style={{ marginTop: 0, gap: '8px' }}>
          <button
            className="continue-option collapsed"
            onClick={handleLinkedInAuth}
            aria-label="Continue with LinkedIn"
          >
            <i className="fab fa-linkedin" style={{ color: '#0077b5' }} />
            <span>Continue with LinkedIn</span>
          </button>
          <button
            className="continue-option collapsed"
            onClick={handleMicrosoftAuth}
            aria-label="Continue with Microsoft"
          >
            <i className="fab fa-microsoft" style={{ color: '#00a4ef' }} />
            <span>Continue with Microsoft</span>
          </button>
          <button
            className={`continue-option email-button ${showEmailForm ? 'expanded' : 'collapsed'}`}
            onClick={toggleEmailForm}
            aria-label="Continue with Email"
          >
            <i className="fas fa-envelope" />
            <span>Continue with Email</span>
          </button>
        </div>
      </div>

      {submitMessage && (
        <div
          ref={messageRef}
          style={{
            margin: '20px auto',
            padding: '15px',
            background: submitMessage.startsWith('Error') ? '#fee' : '#efe',
            border: `1px solid ${submitMessage.startsWith('Error') ? '#fcc' : '#cfc'}`,
            borderRadius: '8px',
            maxWidth: '400px',
            textAlign: 'center',
          }}
          role="alert"
        >
          {submitMessage}
        </div>
      )}

      <div
        id="email-form"
        className="email-form"
        style={{
          display: showEmailForm ? 'block' : 'none',
          margin: '20px auto 0',
          maxWidth: '400px',
          borderRadius: '8px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.05)',
        }}
      >
        <div className="quick-contact-form">
          <p
            className="form-note"
            style={{ fontWeight: 'bold', fontSize: '1.2em', textAlign: 'center' }}
          >
            Enter Your Details To Send Your Video.
          </p>
          <form id="quickContactForm" onSubmit={handleQuickContactSubmit}>
            <label htmlFor="name" className="sr-only">
              Your Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="* Your Name"
              required
              style={{
                display: 'block',
                width: '100%',
                marginBottom: '10px',
                padding: '12px',
                background: 'white',
                color: 'black',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
              autoComplete="name"
              aria-label="Your Name"
            />
            <br />
            <label htmlFor="email" className="sr-only">
              Your Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="* Your Email"
              required
              style={{
                display: 'block',
                width: '100%',
                marginBottom: '10px',
                padding: '12px',
                background: 'white',
                color: 'black',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
              autoComplete="email"
              aria-label="Your Email"
            />
            <br />
            <label htmlFor="company" className="sr-only">
              Your Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              placeholder="Your Company (optional)"
              style={{
                display: 'block',
                width: '100%',
                marginBottom: '15px',
                padding: '12px',
                background: 'white',
                color: 'black',
                border: '1px solid #ccc',
                borderRadius: '4px',
              }}
              autoComplete="organization"
              aria-label="Your Company"
            />
            <br />
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Video'}
            </button>
          </form>
        </div>
      </div>

      {showHelpModal && (
        <div
          className="help-popup-content"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '400px',
            padding: '20px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            zIndex: 10000,
          }}
        >
          <button
            onClick={() => setShowHelpModal(false)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              background: '#f0f0f0',
              color: '#666',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            aria-label="Close"
          >
            ×
          </button>
          <h3
            id="loom-instructions-title"
            style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1rem' }}
          >
            How to record a Loom:
          </h3>
          <ol
            style={{
              paddingLeft: '20px',
              margin: 0,
              color: '#4A4A4A',
              fontSize: '0.9rem',
              lineHeight: '1.6',
              listStyleType: 'decimal',
            }}
          >
            <li style={{ marginBottom: '10px' }}>
              <a
                href="https://loom.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#667eea', textDecoration: 'none' }}
              >
                Get Loom for free
              </a>
            </li>
            <li style={{ marginBottom: '10px' }}>
              Record a 2-5 minute video
              <br />
              <strong>Tip:</strong> As you show your manual process, use your Mic to explain it.
            </li>
            <li style={{ marginBottom: 0 }}>
              Click &quot;Share&quot; in Loom
              <br />
              Copy the Video URL
              <br />
              Paste on this site
            </li>
          </ol>
        </div>
      )}
      {showHelpModal && (
        <div
          onClick={() => setShowHelpModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.3)',
            zIndex: 9999,
          }}
        />
      )}
    </div>
  );
}
